import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { Team } from '../../types';

interface SearchableTeamSelectProps {
  value: string;
  options: Team[];
  onChange: (value: string) => void;
  placeholder: string;
  className: string;
  disabled?: boolean;
  formatLabel?: (name: string) => string;
  hideIcon?: boolean;
}

export function SearchableTeamSelect({ value, options, onChange, placeholder, className, disabled, formatLabel, hideIcon }: SearchableTeamSelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const rawSelectedName = options.find((o) => o.id === value)?.name || '';
  const selectedName = formatLabel ? formatLabel(rawSelectedName) : rawSelectedName;

  useEffect(() => {
    if (!isOpen) setSearch(selectedName);
  }, [isOpen, selectedName]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? search : selectedName}
          disabled={disabled}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (disabled) return;
            setIsOpen(true);
            setSearch('');
          }}
          placeholder={placeholder}
          className={`${className} pr-8 truncate ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        {!hideIcon && <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />}
      </div>
      {isOpen && (
        <div className="absolute z-[999] w-full mt-1 max-h-60 overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl">
          {filtered.map((o) => (
            <div
              key={o.id}
              className="px-3 py-2.5 text-xs md:text-sm text-white font-bold hover:bg-yellow-400 hover:text-black transition-colors cursor-pointer border-b border-white/10 last:border-0 truncate"
              onClick={() => {
                onChange(o.id);
                setIsOpen(false);
              }}
            >
              {formatLabel ? formatLabel(o.name) : o.name}
            </div>
          ))}
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400 italic">No se encontraron equipos...</div>}
        </div>
      )}
    </div>
  );
}
